import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Build as BuildIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import {
  CAPABILITIES,
  getCapabilityDisplayName,
  getCapabilityDescription,
  ROLE_CAPABILITY_DEFAULTS,
} from '../../utils/permissions';

const CapabilityManager = () => {
  const { 
    userCapabilities, 
    primaryRole,
    toggleUserCapability, 
    updatePrimaryRole,
    loading 
  } = useAuth();
  
  const [localCapabilities, setLocalCapabilities] = useState([]);
  const [localPrimaryRole, setLocalPrimaryRole] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, capability: null, action: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (userCapabilities) {
      setLocalCapabilities([...userCapabilities]);
    }
    if (primaryRole) {
      setLocalPrimaryRole(primaryRole);
    }
  }, [userCapabilities, primaryRole]);

  const getCapabilityIcon = (capability) => {
    const icons = {
      [CAPABILITIES.CAN_APPLY_FOR_JOBS]: <PersonIcon />,
      [CAPABILITIES.CAN_BOOK_SERVICES]: <BuildIcon />,
      [CAPABILITIES.CAN_PROVIDE_SERVICES]: <BuildIcon />,
      [CAPABILITIES.CAN_POST_JOBS]: <BusinessIcon />,
    };
    return icons[capability] || <InfoIcon />;
  };

  const getCapabilityColor = (capability) => {
    const colors = {
      [CAPABILITIES.CAN_APPLY_FOR_JOBS]: 'primary',
      [CAPABILITIES.CAN_BOOK_SERVICES]: 'info',
      [CAPABILITIES.CAN_PROVIDE_SERVICES]: 'success',
      [CAPABILITIES.CAN_POST_JOBS]: 'secondary',
    };
    return colors[capability] || 'default';
  };

  const handleCapabilityToggle = (capability) => {
    const isCurrentlyEnabled = localCapabilities.includes(capability);
    const action = isCurrentlyEnabled ? 'disable' : 'enable';
    
    // Show confirmation dialog for important changes
    if (isCurrentlyEnabled && localCapabilities.length === 1) {
      setSnackbar({
        open: true,
        message: 'You must have at least one capability enabled.',
        severity: 'warning'
      });
      return;
    }

    setConfirmDialog({
      open: true,
      capability,
      action
    });
  };

  const confirmCapabilityToggle = async () => {
    const { capability, action } = confirmDialog;
    setIsUpdating(true);
    
    try {
      await toggleUserCapability(capability);
      
      // Update local state
      if (action === 'enable') {
        setLocalCapabilities(prev => [...prev, capability]);
      } else {
        setLocalCapabilities(prev => prev.filter(cap => cap !== capability));
      }
      
      setSnackbar({
        open: true,
        message: `Successfully ${action}d ${getCapabilityDisplayName(capability)}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to ${action} capability: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsUpdating(false);
      setConfirmDialog({ open: false, capability: null, action: null });
    }
  };

  const handlePrimaryRoleChange = async (newRole) => {
    if (newRole === localPrimaryRole) return;
    
    setIsUpdating(true);
    try {
      await updatePrimaryRole(newRole);
      setLocalPrimaryRole(newRole);
      setSnackbar({
        open: true,
        message: `Primary role updated to ${newRole}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to update primary role: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getRecommendedCapabilities = () => {
    if (!localPrimaryRole) return [];
    return ROLE_CAPABILITY_DEFAULTS[localPrimaryRole] || [];
  };

  const enableRecommendedCapabilities = async () => {
    const recommended = getRecommendedCapabilities();
    const toEnable = recommended.filter(cap => !localCapabilities.includes(cap));
    
    if (toEnable.length === 0) {
      setSnackbar({
        open: true,
        message: 'All recommended capabilities are already enabled.',
        severity: 'info'
      });
      return;
    }

    setIsUpdating(true);
    try {
      for (const capability of toEnable) {
        await toggleUserCapability(capability);
      }
      
      setLocalCapabilities(prev => [...new Set([...prev, ...toEnable])]);
      setSnackbar({
        open: true,
        message: `Enabled ${toEnable.length} recommended capabilities`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to enable some capabilities: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Primary Role Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Primary Role
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your primary role determines your default dashboard and navigation experience.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Primary Role</InputLabel>
            <Select
              value={localPrimaryRole}
              label="Primary Role"
              onChange={(e) => handlePrimaryRoleChange(e.target.value)}
              disabled={isUpdating}
            >
              <MenuItem value="jobseeker">Job Seeker</MenuItem>
              <MenuItem value="serviceprovider">Service Provider</MenuItem>
              <MenuItem value="employer">Employer</MenuItem>
            </Select>
          </FormControl>

          {localPrimaryRole && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Recommended capabilities for {localPrimaryRole}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {getRecommendedCapabilities().map(capability => (
                  <Chip
                    key={capability}
                    label={getCapabilityDisplayName(capability)}
                    size="small"
                    color={localCapabilities.includes(capability) ? 'success' : 'default'}
                    variant={localCapabilities.includes(capability) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={enableRecommendedCapabilities}
                disabled={isUpdating}
              >
                Enable Recommended Capabilities
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Capability Management */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manage Your Capabilities
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enable or disable capabilities to customize your platform experience. 
            You can change these settings at any time.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Flexibility Rule:</strong> Instead of fixed role silos, you can enable/disable 
              capabilities in your profile. For example, a Job Seeker can toggle "I also want to provide services."
            </Typography>
          </Alert>

          <List>
            {Object.values(CAPABILITIES).map((capability, index) => (
              <React.Fragment key={capability}>
                <ListItem>
                  <ListItemIcon>
                    {getCapabilityIcon(capability)}
                  </ListItemIcon>
                  <ListItemText
                    primary={getCapabilityDisplayName(capability)}
                    secondary={getCapabilityDescription(capability)}
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={localCapabilities.includes(capability)}
                          onChange={() => handleCapabilityToggle(capability)}
                          disabled={isUpdating}
                          color={getCapabilityColor(capability)}
                        />
                      }
                      label=""
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                {index < Object.values(CAPABILITIES).length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {/* Current Status */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Active Capabilities:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {localCapabilities.map(capability => (
                <Chip
                  key={capability}
                  label={getCapabilityDisplayName(capability)}
                  size="small"
                  color={getCapabilityColor(capability)}
                  icon={<CheckCircleIcon />}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, capability: null, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Capability {confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} the capability "
            {confirmDialog.capability && getCapabilityDisplayName(confirmDialog.capability)}"?
          </Typography>
          {confirmDialog.action === 'disable' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Disabling this capability will remove access to related features and may affect your dashboard experience.
            </Alert>
          )}
          {confirmDialog.action === 'enable' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Enabling this capability will give you access to new features and dashboard sections.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, capability: null, action: null })}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmCapabilityToggle}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
          >
            {confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CapabilityManager;